import type { LatLngBounds } from './places.js'

/**
 * Axis-aligned bounds from GeoJSON positions `[lng, lat][]` (WGS84).
 */
export function latLngBoundsFromLngLatCoordinates(
  coords: [number, number][],
): LatLngBounds | null {
  if (!coords.length) return null
  let minLat = Infinity
  let maxLat = -Infinity
  let minLng = Infinity
  let maxLng = -Infinity
  for (const [lng, lat] of coords) {
    minLat = Math.min(minLat, lat)
    maxLat = Math.max(maxLat, lat)
    minLng = Math.min(minLng, lng)
    maxLng = Math.max(maxLng, lng)
  }
  return {
    southWest: { lat: minLat, lng: minLng },
    northEast: { lat: maxLat, lng: maxLng },
  }
}

/**
 * Same as {@link latLngBoundsFromLngLatCoordinates} for `{ lat, lng }` points (domain order).
 */
export function latLngBoundsFromPoints(
  points: ReadonlyArray<{ lat: number; lng: number }>,
): LatLngBounds | null {
  if (points.length === 0) return null
  let minLat = Infinity
  let maxLat = -Infinity
  let minLng = Infinity
  let maxLng = -Infinity
  for (const p of points) {
    minLat = Math.min(minLat, p.lat)
    maxLat = Math.max(maxLat, p.lat)
    minLng = Math.min(minLng, p.lng)
    maxLng = Math.max(maxLng, p.lng)
  }
  return {
    southWest: { lat: minLat, lng: minLng },
    northEast: { lat: maxLat, lng: maxLng },
  }
}

export function unionLatLngBounds(
  a: LatLngBounds | null | undefined,
  b: LatLngBounds | null | undefined,
): LatLngBounds | null {
  if (!a) return b ?? null
  if (!b) return a
  return {
    southWest: {
      lat: Math.min(a.southWest.lat, b.southWest.lat),
      lng: Math.min(a.southWest.lng, b.southWest.lng),
    },
    northEast: {
      lat: Math.max(a.northEast.lat, b.northEast.lat),
      lng: Math.max(a.northEast.lng, b.northEast.lng),
    },
  }
}

export function unionManyLatLngBounds(
  list: ReadonlyArray<LatLngBounds | null | undefined>,
): LatLngBounds | null {
  let acc: LatLngBounds | null = null
  for (const b of list) {
    acc = unionLatLngBounds(acc, b)
  }
  return acc
}
